export type DocumentStatus = "active" | "archived" | "trashed";

export interface LocalDocument {
  id: string;
  driveFileId?: string;
  title: string;
  text: string;
  createdAt: number;
  updatedAt: number;
  status: DocumentStatus;
  archivedAt?: number;
  trashedAt?: number;
}

const DATABASE_NAME = "whisperwriter-local";
const DATABASE_VERSION = 1;
const DOCUMENT_STORE = "documents";

let databasePromise: Promise<IDBDatabase> | null = null;

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed."));
  });
}

function transactionComplete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("IndexedDB transaction failed."));
    transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB transaction aborted."));
  });
}

function openDatabase(): Promise<IDBDatabase> {
  if (databasePromise) {
    return databasePromise;
  }

  databasePromise = new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DATABASE_NAME, DATABASE_VERSION);

    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(DOCUMENT_STORE)) {
        const store = database.createObjectStore(DOCUMENT_STORE, { keyPath: "id" });
        store.createIndex("updatedAt", "updatedAt");
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Unable to open local document database."));
  });

  return databasePromise;
}

export function createLocalDocument(values: Partial<LocalDocument> = {}): LocalDocument {
  const now = Date.now();

  return {
    id:
      values.id ??
      globalThis.crypto?.randomUUID?.() ??
      `${now}-${Math.random().toString(16).slice(2)}`,
    driveFileId: values.driveFileId,
    title: values.title ?? "",
    text: values.text ?? "",
    createdAt: values.createdAt ?? now,
    updatedAt: values.updatedAt ?? now,
    status: values.status ?? "active",
    archivedAt: values.archivedAt,
    trashedAt: values.trashedAt
  };
}

function normalizeDocument(document: LocalDocument): LocalDocument {
  return createLocalDocument({
    ...document,
    id: document.id,
    driveFileId: document.driveFileId,
    title: document.title ?? "",
    text: document.text ?? "",
    createdAt: document.createdAt ?? Date.now(),
    updatedAt: document.updatedAt ?? document.createdAt ?? Date.now(),
    status: document.status ?? "active"
  });
}

export async function listDocuments(): Promise<LocalDocument[]> {
  const database = await openDatabase();
  const transaction = database.transaction(DOCUMENT_STORE, "readonly");
  const store = transaction.objectStore(DOCUMENT_STORE);
  const documents = await requestResult<LocalDocument[]>(store.getAll());

  return documents.map(normalizeDocument).sort((left, right) => right.updatedAt - left.updatedAt);
}

export async function saveDocument(document: LocalDocument): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction(DOCUMENT_STORE, "readwrite");
  transaction.objectStore(DOCUMENT_STORE).put(normalizeDocument(document));
  await transactionComplete(transaction);
}

export async function deleteDocument(id: string): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction(DOCUMENT_STORE, "readwrite");
  transaction.objectStore(DOCUMENT_STORE).delete(id);
  await transactionComplete(transaction);
}

export async function deleteDocuments(ids: string[]): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction(DOCUMENT_STORE, "readwrite");
  const store = transaction.objectStore(DOCUMENT_STORE);

  for (const id of ids) {
    store.delete(id);
  }

  await transactionComplete(transaction);
}
